import type { DataContext } from '@life-os/data';
import {
  type FeatureRegistry,
  type SearchResult,
  type SearchResultGroup,
  parseSearchQuery,
  redactByDefault,
} from '@life-os/app-core';

function systemSurface(s: ReturnType<FeatureRegistry['all']>[number]): string {
  return s.surface.kind === 'route' ? s.surface.path : `#${s.surface.name}`;
}

/** Redaction-aware preview: sensitive content shows a safe placeholder. */
function preview(text: string, privacy: SearchResult['privacyClass']): string {
  if (redactByDefault(privacy)) return 'Private — open to view';
  return text.length > 80 ? `${text.slice(0, 77)}…` : text;
}

/**
 * One search across systems (registry) + live objects (tasks, captures).
 * The raw query may carry filter tokens (privacy:, kind:, group:, maturity:,
 * platform:) which narrow results. This is the single discovery path — it is
 * why no feature can stay hidden.
 */
export async function searchAll(
  rawQuery: string,
  registry: FeatureRegistry,
  data: DataContext,
): Promise<readonly SearchResultGroup[]> {
  const { text, filters = {} } = parseSearchQuery(rawQuery);
  const q = text.trim().toLowerCase();
  const groups: SearchResultGroup[] = [];

  const wantsKind = (k: SearchResult['kind']) => filters.kind === undefined || filters.kind === k;

  // Systems — always searchable, even with empty text (acts as a directory).
  if (wantsKind('system')) {
    const systems = registry
      .all()
      .filter((s) => q === '' || s.label.toLowerCase().includes(q) || s.shortPurpose.toLowerCase().includes(q))
      .filter((s) => filters.group === undefined || s.group === filters.group)
      .filter((s) => filters.maturity === undefined || s.maturity === filters.maturity)
      .filter((s) => filters.privacyClass === undefined || s.privacyClass === filters.privacyClass)
      .filter((s) => filters.platform === undefined || s.platformAvailability[filters.platform] === 'available')
      .map<SearchResult>((s) => ({
        id: String(s.id),
        kind: 'system',
        title: s.label,
        preview: s.shortPurpose,
        privacyClass: s.privacyClass,
        surface: systemSurface(s),
      }));
    if (systems.length) groups.push({ kind: 'system', results: systems });
  }

  const matchesPrivacy = (p: SearchResult['privacyClass']) =>
    filters.privacyClass === undefined || filters.privacyClass === p;

  if (q !== '') {
    if (wantsKind('task')) {
      const tasks = await data.repositories.tasks.listByLane('now');
      const next = await data.repositories.tasks.listByLane('next');
      const taskResults = [...tasks, ...next]
        .filter((t) => t.title.toLowerCase().includes(q) && matchesPrivacy(t.privacyClass))
        .map<SearchResult>((t) => ({
          id: String(t.id),
          kind: 'task',
          title: t.title,
          preview: preview(t.title, t.privacyClass),
          privacyClass: t.privacyClass,
          surface: '/tasks',
        }));
      if (taskResults.length) groups.push({ kind: 'task', results: taskResults });
    }

    if (wantsKind('capture')) {
      const captures = await data.repositories.capture.listUnreviewed();
      const captureResults = captures
        .filter((c) => c.rawText.toLowerCase().includes(q) && matchesPrivacy(c.privacyClass))
        .map<SearchResult>((c) => ({
          id: String(c.id),
          kind: 'capture',
          title: preview(c.rawText, c.privacyClass),
          preview: preview(c.rawText, c.privacyClass),
          privacyClass: c.privacyClass,
          surface: '/capture',
        }));
      if (captureResults.length) groups.push({ kind: 'capture', results: captureResults });
    }
  }

  return groups;
}
